import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import './TotalStudents.css';

const TotalStudents = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [filterType, setFilterType] = useState(''); // State for filter type (month or year)
  const [filterValue, setFilterValue] = useState(''); // State for the selected filter value
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const itemsPerPage = 10; // Number of items per page

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const { data } = await axios.get(`${api.getCourses}?instructor=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const studentDetails = data.courses.flatMap(course =>
          course.enrolledStudents.map(student => ({
            studentName: student.name,
            courseName: course.title,
            actualPrice: course.actualPrice,
            purchasedPrice: course.offerPrice,
            purchasedDate: student.purchasedDate || 'N/A',
            rating: student.rating || 'N/A',
          })),
        );

        setStudents(studentDetails);
        setFilteredStudents(studentDetails);
      } catch (err) {
        console.error('Error fetching students:', err);
      }
    };

    fetchStudents();
  }, [token]);

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase().trim().replace(/\s+/g, ' ');
    setSearchQuery(query);

    const filtered = students.filter((student) => {
      const studentName = student.studentName?.toLowerCase().trim().replace(/\s+/g, ' ') || '';
      const courseName = student.courseName?.toLowerCase().trim().replace(/\s+/g, ' ') || '';
      return studentName.includes(query) || courseName.includes(query);
    });

    setFilteredStudents(filtered);
    setCurrentPage(1); // Reset to the first page after filtering
  };

  const handleFilterChange = (type, value) => {
    setFilterType(type);
    setFilterValue(value);

    const filtered = students.filter((student) => {
      const purchasedDate = student.purchasedDate !== 'N/A' ? new Date(student.purchasedDate) : null;

      if (type === 'month') {
        return purchasedDate?.getMonth() + 1 === parseInt(value);
      } else if (type === 'year') {
        return purchasedDate?.getFullYear() === parseInt(value);
      }

      return true;
    });

    setFilteredStudents(filtered);
    setCurrentPage(1); // Reset to the first page after filtering
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Sort by purchased date (latest first) and paginate
  const currentStudents = [...filteredStudents]
    .sort((a, b) => {
      const dateA = a.purchasedDate !== 'N/A' ? new Date(a.purchasedDate) : null;
      const dateB = b.purchasedDate !== 'N/A' ? new Date(b.purchasedDate) : null;

      if (!dateA) return 1; // If dateA is null, place it later
      if (!dateB) return -1; // If dateB is null, place it earlier
      return dateB - dateA; // Sort in descending order
    })
    .slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div>
      <div className="total-students-container">
        <button
          className="total-students-close-button"
          onClick={() => navigate('/performance')}
        >
          ×
        </button>
        <h1>Total Students</h1>

        <div className="search-and-filter-container">
          <div className="total-student-search-bar-container">
            <input
              type="text"
              className="total-student-search-bar"
              placeholder="Search by student name or course name..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="total-student-filter-container">
            <select
              className="total-student-filter-dropdown"
              onChange={(e) => {
                const selectedType = e.target.value;
                setFilterType(selectedType);
                setFilterValue('');
              }}
            >
              <option value="">Select Filter</option>
              <option value="month">Filter by Month</option>
              <option value="year">Filter by Year</option>
            </select>

            {filterType && (
              <select
                className="total-student-filter-dropdown"
                value={filterValue}
                onChange={(e) => handleFilterChange(filterType, e.target.value)}
              >
                <option value="">All {filterType === 'month' ? 'Months' : 'Years'}</option>
                {filterType === 'month' &&
                  Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                {filterType === 'year' &&
                  Array.from(new Set(students.map((student) => {
                    const purchasedDate = student.purchasedDate !== 'N/A' ? new Date(student.purchasedDate) : null;
                    return purchasedDate?.getFullYear();
                  })))
                    .filter((year) => year)
                    .map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
              </select>
            )}
          </div>
        </div>

        <table className="students-table">
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Course Name</th>
              <th>Actual Price</th>
              <th>Purchased Price</th>
              <th>Purchased Date</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            {currentStudents.map((student, index) => (
              <tr key={index}>
                <td>{student.studentName}</td>
                <td>{student.courseName}</td>
                <td>{student.actualPrice}</td>
                <td>{student.purchasedPrice === 0 ? 'Free' : student.purchasedPrice}</td>
                <td>
                  {student.purchasedDate
                    ? new Date(student.purchasedDate).toLocaleDateString('en-GB').replace(/\//g, '-')
                    : 'N/A'}
                </td>
                <td>
                  {student.rating !== 'N/A' ? (
                    <div className="student-rating">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className={i < student.rating ? 'star filled' : 'star'}>
                          ★
                        </span>
                      ))}
                      <span className="rating-value">({student.rating})</span>
                    </div>
                  ) : (
                    'Not Rated'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="total-student-pagination-container">
          <button
            className="total-student-pagination-button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="total-student-pagination-info">
            Page {currentPage} of {Math.ceil(filteredStudents.length / itemsPerPage)}
          </span>
          <button
            className="total-student-pagination-button"
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(prev + 1, Math.ceil(filteredStudents.length / itemsPerPage)),
              )
            }
            disabled={currentPage === Math.ceil(filteredStudents.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default TotalStudents;
